import { ArrowRight } from 'lucide-react';
import { Shamsa } from '@/components/persian-ornament';
import { Starfield } from '@/components/starfield';
import { PORTAL_URL } from '@/lib/config';
import { t, useLang, type StringTable } from '@/lib/i18n';

const STRINGS = {
  'hero.eyebrow': {
    en: 'Sovereign intelligence archive · v0.1 preview',
    fa: 'آرشیو اطلاعاتی مستقل · پیش‌نمایش نسخهٔ ۰٫۱',
  },
  'hero.title.lead': {
    en: 'A working archive for',
    fa: 'یک آرشیو کاری برای',
  },
  'hero.title.accent': {
    en: 'defensible analysis',
    fa: 'تحلیل قابل دفاع',
  },
  'hero.title.tail': { en: '.', fa: '.' },
  'hero.subtitle': {
    en: 'Graph-native retrieval, AI synthesis with calibrated confidence, and an evidence trail behind every claim. Built for analysts who must defend the answer they give — bilingual at the architecture, not as an afterthought.',
    fa: 'بازیابی مبتنی بر گراف، هم‌نهاد سازیِ هوش مصنوعی با اطمینان درجه‌بندی‌شده، و ردِ شواهد در پی هر ادعا. ساخته‌شده برای تحلیل‌گرانی که باید پاسخ خود را دفاع کنند — دوزبانه در معماری، نه به‌عنوان روکشی پس از طراحی.',
  },
  'hero.cta.primary': { en: 'Request access', fa: 'درخواست دسترسی' },
  'hero.cta.secondary': { en: 'See how it works', fa: 'نحوه کارکرد' },
  'hero.portal.prefix': { en: 'Already credentialed?', fa: 'حساب کاربری دارید؟' },
  'hero.portal.link': { en: 'Launch portal', fa: 'ورود به پورتال' },
} satisfies StringTable<string>;

export function Hero() {
  const lang = useLang();

  return (
    <section className="vellum-grain relative overflow-hidden">
      {/* Faint girih tessellation — Persian manuscript geometry scattered
       *  across the deep canvas so "dark" doesn't read as empty. Extra
       *  low opacity so it lives under the starfield and never competes
       *  with copy; masked out at the bottom to hand off cleanly. */}
      <div
        aria-hidden="true"
        className="girih-tile pointer-events-none absolute inset-0 opacity-[0.14]"
        style={{
          maskImage:
            'radial-gradient(ellipse 120% 80% at 50% 30%, black 0%, black 45%, transparent 100%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 120% 80% at 50% 30%, black 0%, black 45%, transparent 100%)',
        }}
      />

      {/* Constellation field — the "Cup of Jamshid" metaphor. 4-point
       *  khatam stars (Persian manuscript "sparkles"), a fraction tinted
       *  gold like the bright classical Persian stars (Soheyl/Canopus,
       *  Tir/Sirius). Bottom fade-mask hands off cleanly to the next
       *  section. */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          maskImage:
            'linear-gradient(to bottom, black 0%, black 70%, transparent 100%)',
          WebkitMaskImage:
            'linear-gradient(to bottom, black 0%, black 70%, transparent 100%)',
        }}
      >
        <Starfield density={1.1} linkDistance={140} intensity={0.55} />
      </div>

      <Shamsa
        size={560}
        className="pointer-events-none absolute -end-40 top-1/2 hidden -translate-y-1/2 text-[var(--color-accent)] opacity-[0.22] md:block lg:-end-24 lg:opacity-[0.28]"
      />

      <div className="relative mx-auto max-w-[88rem] px-6 pb-20 pt-24 md:pb-28 md:pt-32">
        <div className="max-w-3xl">
          <div className="mb-5 inline-flex items-center gap-2 rounded-sm border border-[var(--color-border)] bg-[var(--color-surface)]/70 px-3 py-1 text-xxs text-[var(--color-ink-muted)] backdrop-blur-sm">
            <span
              aria-hidden="true"
              className="block size-1 rounded-full bg-[var(--color-accent-bright)]"
            />
            <span className="uppercase tracking-[0.18em]">
              {t(STRINGS, 'hero.eyebrow', lang)}
            </span>
          </div>

          <h1 className="text-5xl leading-[1.05] md:text-7xl">
            {t(STRINGS, 'hero.title.lead', lang)}{' '}
            <span className="text-[var(--color-accent)]">
              {t(STRINGS, 'hero.title.accent', lang)}
            </span>
            {t(STRINGS, 'hero.title.tail', lang)}
          </h1>

          <p className="mt-5 max-w-2xl text-lg text-[var(--color-ink-secondary)] md:text-xl">
            {t(STRINGS, 'hero.subtitle', lang)}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href={`${PORTAL_URL}/sign-up`}
              target="_self"
              className="inline-flex items-center gap-2 rounded-md bg-[var(--color-accent)] px-5 py-2.5 text-sm font-semibold text-[var(--color-canvas)] shadow-[0_0_0_1px_var(--color-accent-bright),0_8px_32px_-8px_color-mix(in_oklab,var(--color-accent)_60%,transparent)] transition-all hover:bg-[var(--color-accent-bright)]"
            >
              {t(STRINGS, 'hero.cta.primary', lang)}
              <ArrowRight className="icon-directional size-3.5" />
            </a>
            <a
              href="#how"
              className="inline-flex items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)]/70 px-5 py-2.5 text-sm text-[var(--color-ink-secondary)] backdrop-blur-sm transition-colors hover:border-[var(--color-accent)] hover:text-[var(--color-ink)]"
            >
              {t(STRINGS, 'hero.cta.secondary', lang)}
            </a>
          </div>

          <p className="mt-6 text-xs text-[var(--color-ink-muted)]">
            {t(STRINGS, 'hero.portal.prefix', lang)}{' '}
            <a
              href={PORTAL_URL}
              target="_self"
              className="underline decoration-[var(--color-accent)] underline-offset-4 hover:text-[var(--color-ink)]"
            >
              {t(STRINGS, 'hero.portal.link', lang)} →
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}
